import { Builder } from '@builder.io/react'
import React, { useState, useEffect } from 'react'

// https://react-slick.neostack.com/docs/get-started
import Slider from 'react-slick'

enum ImageSide {
  Left = 'left',
  Right = 'right'
}

interface Testimonial {
  backgroundColor: string
  textColor: string
  imgSrc: any
  testimonialText: string
  author: string
  designation: string
  imageSide: ImageSide
  altText: string
}

interface Props {
  backgroundColor: string
  enableQuote: boolean
  testimonials: Testimonial[]
  autoplayTime: number
  quoteColor: string
}

const TestimonialOne = ({ backgroundColor, enableQuote, testimonials, autoplayTime, quoteColor }: Props) => {
  const [carouselSettings, setCarouselSettings] = useState({
    dots: false,
    arrows: false,
    autoplay: true,
    autoplaySpeed: autoplayTime
  })

  useEffect(() => {
    setCarouselSettings({
      dots: false,
      arrows: false,
      autoplay: true,
      autoplaySpeed: autoplayTime
    })
  }, [autoplayTime, backgroundColor])

  const getQuote = () => {
    if (!enableQuote) return null
    return (
      <svg
        className="h-12 w-12 text-white opacity-25"
        fill="currentColor"
        viewBox="0 0 32 32"
        aria-hidden="true"
        style={{ color: quoteColor }}
      >
        <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
      </svg>
    )
  }

  const getContent = (te: Testimonial) => {
    if (te.imageSide === ImageSide.Left) {
      return (
        <div className="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-3 lg:gap-8 lg:px-8">
          <div className="relative lg:-my-8">
            <div aria-hidden="true" className="inset-x-0 top-0 h-1/2 bg-white lg:hidden" />
            <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:h-full lg:p-0">
              <div className="aspect-w-10 aspect-h-6 overflow-hidden rounded-xl shadow-xl sm:aspect-w-16 sm:aspect-h-7 lg:aspect-none lg:h-full">
                <img
                  className="object-cover lg:h-full lg:w-full"
                  src={
                    te.imgSrc ||
                    'https://images.unsplash.com/photo-1520333789090-1afc82db536a?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2102&q=80'
                  }
                  alt={te.altText}
                />
              </div>
            </div>
          </div>
          <div className="mt-12 lg:col-span-2 lg:m-0 lg:pl-8">
            <div
              className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0 lg:py-20"
              style={{ color: te.textColor }}
            >
              <blockquote>
                <div>
                  {getQuote()}
                  <p className="mt-6 text-lg font-medium text-white" style={{ color: te.textColor }}>
                    {te.testimonialText}
                  </p>
                </div>
                <footer className="mt-6">
                  <p className="text-base font-medium text-white" style={{ color: te.textColor }}>
                    {te.author}
                  </p>
                  <p className="text-base font-medium text-indigo-100" style={{ color: te.textColor }}>
                    {te.designation}
                  </p>
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-3 lg:gap-8 lg:px-8">
        <div className="mt-12 sm:mb-4 lg:col-span-2 lg:m-0 lg:pl-8">
          <div
            className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0 lg:py-20"
            style={{ color: te.textColor }}
          >
            <blockquote>
              <div>
                {getQuote()}
                <p className="mt-6 text-lg font-medium text-white" style={{ color: te.textColor }}>
                  {te.testimonialText}
                </p>
              </div>
              <footer className="mt-6">
                <p className="text-base font-medium text-white" style={{ color: te.textColor }}>
                  {te.author}
                </p>
                <p className="text-base font-medium text-indigo-100" style={{ color: te.textColor }}>
                  {te.designation}
                </p>
              </footer>
            </blockquote>
          </div>
        </div>
        <div className="relative lg:-my-8">
          <div aria-hidden="true" className="inset-x-0 top-0 h-1/2 bg-white lg:hidden" />
          <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:h-full lg:p-0">
            <div className="aspect-w-10 aspect-h-6 overflow-hidden rounded-xl shadow-xl sm:aspect-w-16 sm:aspect-h-7 lg:aspect-none lg:h-full">
              <img
                className="object-cover lg:h-full lg:w-full"
                src={
                  te.imgSrc ||
                  'https://images.unsplash.com/photo-1520333789090-1afc82db536a?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2102&q=80'
                }
                alt={te.altText}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getTestimonials = () => {
    return testimonials.map((te, i) => {
      return (
        <div>
          <div
            className="bg-white pt-4 lg:py-16"
            style={{ backgroundColor: backgroundColor }}
            key={`${te.author}-${i}`}
          >
            <div
              className="bg-indigo-600 pb-16 lg:relative lg:z-10 lg:pb-0"
              style={{ backgroundColor: te.backgroundColor }}
            >
              {getContent(te)}
            </div>
          </div>
        </div>
      )
    })
  }

  return <Slider {...carouselSettings}>{getTestimonials()}</Slider>
}

export default TestimonialOne

Builder.registerComponent(TestimonialOne, {
  name: 'TestimonialOne',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'enableQuote',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'quoteColor',
      type: 'color',
      defaultValue: 'gray'
    },
    {
      name: 'autoplayTime',
      type: 'number',
      defaultValue: 3000
    },
    {
      name: 'testimonials',
      type: 'list',
      defaultValue: [
        {
          backgroundColor: '#424dd7',
          textColor: 'white',
          imgSrc: null,
          testimonialText:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed urna nulla vitae laoreet augue.',
          author: 'Judith Bekker',
          designation: 'CEO at Something',
          imageSide: 'left',
          altText: ''
        }
      ],
      subFields: [
        { name: 'backgroundColor', type: 'color', defaultValue: '#424dd7' },
        { name: 'textColor', type: 'color', defaultValue: 'white' },
        { name: 'imgSrc', type: 'file', defaultValue: null },
        {
          name: 'testimonialText',
          type: 'string',
          defaultValue: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed urna nulla vitae laoreet augue.'
        },
        { name: 'author', type: 'string', defaultValue: 'Judith Bekker' },
        { name: 'designation', type: 'string', defaultValue: 'CEO at Something' },
        {
          name: 'imageSide',
          type: 'string',
          enum: ['left', 'right'],
          defaultValue: 'left'
        },
        { name: 'altText', type: 'text', defaultValue: '' }
      ]
    }
  ]
})
